<script setup>
import { computed } from 'vue';
import { groupIN } from '../data/format.js';

/** Holds a plain number, displays it with Indian digit grouping */
const model = defineModel({ type: [Number, String], default: '' });
const props = defineProps({
  id: { type: String, default: null },
  /** md or lg */
  size: { type: String, default: 'lg' },
  min: { type: Number, default: 1 },
  max: { type: Number, default: 50000 },
  invalid: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  /** Light surface or dark tile */
  surface: { type: String, default: 'light' },
});
const shown = computed(() => groupIN(model.value));
const onInput = (e) => {
  const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
  model.value = digits === '' ? '' : Number(digits);
  e.target.value = groupIN(model.value);
};
const over = computed(() => model.value !== '' && (model.value > props.max || model.value < props.min));
</script>

<template>
  <div class="pd-amount" :class="[`pd-amount--${size}`, `pd-amount--${surface}`, { 'is-invalid': invalid || over, 'is-readonly': readonly }]">
    <span class="pd-amount__cur" aria-hidden="true">₹</span>
    <input
      :id="id"
      class="pd-amount__el"
      inputmode="numeric"
      :value="shown"
      placeholder="0"
      :readonly="readonly"
      :aria-invalid="invalid || over || undefined"
      aria-label="Amount in rupees"
      @input="onInput"
    />
    <span class="pd-amount__code">INR</span>
  </div>
</template>

<style scoped>
.pd-amount {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
  height: 84px;
  border-radius: var(--pd-r-md);
  border: 1px solid var(--pd-line);
  background: var(--pd-white);
  transition: border-color var(--pd-fast) var(--pd-ease), box-shadow var(--pd-fast) var(--pd-ease);
}
.pd-amount:focus-within { border-color: var(--pd-line-active); box-shadow: var(--pd-ring); }
.pd-amount--md { height: 56px; padding: 0 16px; }
.pd-amount--dark { background: var(--pd-ink-2); border-color: var(--pd-ink-line); color: var(--pd-white); }
.pd-amount.is-invalid { border-color: var(--pd-danger); }
.pd-amount.is-readonly { background: var(--pd-cream); }
.pd-amount__cur { font-size: 2rem; font-weight: 800; }
.pd-amount--md .pd-amount__cur { font-size: 1.25rem; }
.pd-amount__el {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
}
.pd-amount--md .pd-amount__el { font-size: 1.25rem; letter-spacing: -0.02em; }
.pd-amount__el::placeholder { color: var(--pd-line-strong); }
.pd-amount__code { font-size: 0.75rem; font-weight: 800; letter-spacing: 0.12em; color: var(--pd-subtle); }
@media (max-width: 560px) {
  .pd-amount { height: 72px; }
  .pd-amount__el { font-size: 2rem; }
}
</style>
